import React, { useEffect, useState, useMemo } from 'react';
import { emailApiService, EmailThread } from '../../services/email/emailApiService';
import { emailPollingService } from '../../services/email/emailPollingService';
import { Mail, MessageSquare, Search, RefreshCw } from 'lucide-react';
import { PaginationBar } from './PaginationBar';

const PAGE_SIZE = 20;

interface EmailThreadListProps {
    tenderId?: string;
    onSelectThread: (threadId: string) => void;
    selectedThreadId?: string;
    refreshTrigger?: number;
}

function getInitials(name: string) {
    return name
        .split(' ')
        .slice(0, 2)
        .map(w => w[0])
        .join('')
        .toUpperCase();
}

const AVATAR_COLORS = [
    'bg-blue-500', 'bg-purple-500', 'bg-emerald-500',
    'bg-orange-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500',
];

function avatarColor(name: string) {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function formatDate(dateStr: string) {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export const EmailThreadList: React.FC<EmailThreadListProps> = ({
    tenderId,
    onSelectThread,
    selectedThreadId,
    refreshTrigger,
}) => {
    const [threads, setThreads] = useState<EmailThread[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);

    const fetchThreads = async (silent = false) => {
        try {
            if (silent) setRefreshing(true);
            else setLoading(true);
            const data = await emailApiService.getThreads(tenderId);
            setThreads(data);
            setPage(1);
        } catch (error) {
            console.error('Error fetching threads:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchThreads();
        const unsubscribe = emailPollingService.subscribe(() => fetchThreads(true));
        return () => unsubscribe();
    }, [tenderId, refreshTrigger]);

    useEffect(() => { setPage(1); }, [search]);

    const filtered = useMemo(() => {
        if (!search.trim()) return threads;
        const q = search.toLowerCase();
        return threads.filter(t =>
            t.subject?.toLowerCase().includes(q) ||
            t.retailer?.name?.toLowerCase().includes(q) ||
            t.tender?.opportunity_name?.toLowerCase().includes(q)
        );
    }, [threads, search]);

    const paginated = useMemo(() =>
        filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize]
    );

    return (
        <div className="flex flex-col h-full bg-white rounded-lg border overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center gap-3 px-4 py-3 border-b bg-gray-50/80">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by retailer, subject or tender..."
                        className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-shadow"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                <button
                    onClick={() => fetchThreads(true)}
                    disabled={refreshing}
                    className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                    title="Refresh"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                    {filtered.length} thread{filtered.length !== 1 ? 's' : ''}
                </span>
            </div>

            {/* Thread list */}
            <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
                {loading ? (
                    <div className="divide-y divide-gray-100">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="flex items-start gap-3 px-4 py-3 animate-pulse">
                                <div className="w-9 h-9 rounded-full bg-gray-200 shrink-0" />
                                <div className="flex-1 min-w-0 space-y-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="h-3.5 bg-gray-200 rounded w-28" />
                                        <div className="h-3 bg-gray-100 rounded w-10" />
                                    </div>
                                    <div className="h-3 bg-gray-200 rounded w-48" />
                                    <div className="h-3 bg-gray-100 rounded w-20" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : paginated.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full py-20 text-gray-400">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                            <Mail className="w-7 h-7 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">
                            {search ? 'No threads match your search' : 'No email threads yet'}
                        </p>
                        {!search && (
                            <p className="text-xs text-gray-400 mt-1">
                                Sent emails and retailer replies will appear here
                            </p>
                        )}
                    </div>
                ) : (
                    paginated.map(thread => {
                        const retailerName = thread.retailer?.name || 'Unknown Retailer';
                        const isSelected = selectedThreadId === thread.id;
                        const msgCount = thread.messages?.[0]?.count || 0;

                        return (
                            <div
                                key={thread.id}
                                onClick={() => onSelectThread(thread.id)}
                                className={`
                                    relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors
                                    ${isSelected
                                        ? 'bg-blue-50 border-l-2 border-l-blue-500'
                                        : 'hover:bg-gray-50 border-l-2 border-l-transparent'
                                    }
                                `}
                            >
                                {/* Avatar */}
                                <div className={`w-9 h-9 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold ${avatarColor(retailerName)}`}>
                                    {getInitials(retailerName)}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between gap-2 mb-0.5">
                                        <span className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                                            {retailerName}
                                        </span>
                                        <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                                            {formatDate(thread.last_message_at)}
                                        </span>
                                    </div>

                                    <p className="text-sm text-gray-700 truncate font-medium mb-1">
                                        {thread.subject}
                                    </p>

                                    <div className="flex items-center gap-2 flex-wrap">
                                        {/* Tender badge */}
                                        {!tenderId && thread.tender?.opportunity_name && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-100 truncate max-w-[160px]">
                                                {thread.tender.opportunity_name}
                                            </span>
                                        )}

                                        {/* Status dot */}
                                        <span className="inline-flex items-center gap-1 text-[11px]">
                                            <span className={`w-1.5 h-1.5 rounded-full ${thread.status === 'active' ? 'bg-green-500' : 'bg-gray-300'}`} />
                                            <span className="text-gray-400 capitalize">{thread.status}</span>
                                        </span>

                                        {/* Message count */}
                                        {msgCount > 0 && (
                                            <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
                                                <MessageSquare className="w-3 h-3" />
                                                {msgCount}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Pagination */}
            {filtered.length > 0 && (
                <PaginationBar
                    page={page}
                    pageSize={pageSize}
                    total={filtered.length}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                />
            )}
        </div>
    );
};
